import type { SortingState } from '@tanstack/react-table';

import type { ActiveFilter } from '@/views/Order/components/table/OrderToolbar';
import {
  formatLocalDay,
  restoreActiveFilters,
} from '@/views/Order/utils/orderListFilters';
import {
  COLUMN_IDS,
  DEFAULT_FILTER_IDS,
  DEFAULT_STATUSES,
  FILTER_UNSET,
  destinationKey,
  destinationLabel,
  getPickingJobFilterDefinitions,
  joinValues,
  normalizeFilterValue,
  pickedRatio,
} from './pickingJobFilters';
import type { PickingJobRow } from './types';

const DEFAULT_SORTING: SortingState = [{ id: COLUMN_IDS.age, desc: true }];

export function ageInDays(pickupDate?: string | null): number | null {
  if (!pickupDate) return null;
  const parsed = new Date(`${pickupDate}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return null;
  const today = new Date(`${formatLocalDay(new Date())}T00:00:00Z`);
  return Math.round(
    (today.getTime() - parsed.getTime()) / (1000 * 60 * 60 * 24),
  );
}

function defaultActiveFilters(): ActiveFilter[] {
  return [
    { id: COLUMN_IDS.warehouse, value: joinValues([]) },
    { id: COLUMN_IDS.status, value: joinValues(DEFAULT_STATUSES) },
    // Unset: every pickup date. Only an explicitly picked range narrows it.
    { id: COLUMN_IDS.pickupDate, value: FILTER_UNSET },
  ];
}

/**
 * The persisted filters, validated like the order list does it: values of a
 * filter with static options must still be options, the rest (warehouse,
 * destination, job, pickup date) are trusted. A destination or job no row
 * carries still shows in its facet (see rowFacetOptions), so it can be cleared.
 */
export function readActiveFilters(stored: unknown): ActiveFilter[] {
  const restored = restoreActiveFilters(
    stored,
    getPickingJobFilterDefinitions([], []),
  )
    // A filter id counts once; the first stored entry wins.
    .filter((f, index, all) => all.findIndex((o) => o.id === f.id) === index)
    .map((f) => ({ ...f, value: normalizeFilterValue(f.id, f.value) }));
  const defaults = defaultActiveFilters();
  // The default filters cannot be removed, so they are always there, first.
  return [
    ...defaults.map((d) => restored.find((f) => f.id === d.id) ?? d),
    ...restored.filter((f) => !DEFAULT_FILTER_IDS.includes(f.id)),
  ];
}

export function readSorting(persisted: unknown): SortingState {
  const valid =
    Array.isArray(persisted) &&
    persisted.every(
      (s) => s && typeof s.id === 'string' && typeof s.desc === 'boolean',
    );
  return valid ? (persisted as SortingState) : DEFAULT_SORTING;
}

type SortValue = string | number | null | undefined;

function sortValue(row: PickingJobRow, columnId: string): SortValue {
  switch (columnId) {
    case COLUMN_IDS.job:
      return row.id;
    case COLUMN_IDS.segment:
      return row.segment;
    case COLUMN_IDS.status:
      return row.status;
    case COLUMN_IDS.warehouse:
      return row.warehouseName;
    case COLUMN_IDS.destination:
      return destinationKey(row) ? destinationLabel(row) : null;
    case COLUMN_IDS.pickupDate:
      return row.carrier_pickup_date;
    case COLUMN_IDS.age:
      return row.ageInDays;
    case COLUMN_IDS.picked:
      return pickedRatio(row);
    default:
      return null;
  }
}

export function compareRows(
  a: PickingJobRow,
  b: PickingJobRow,
  columnId: string,
  desc: boolean,
): number {
  const left = sortValue(a, columnId);
  const right = sortValue(b, columnId);
  // Rows with no value sort last regardless of direction, so the direction is
  // applied only to the comparison of two real values.
  if (left == null && right == null) return 0;
  if (left == null) return 1;
  if (right == null) return -1;
  const result =
    typeof left === 'number' && typeof right === 'number'
      ? left - right
      : String(left).localeCompare(String(right));
  return desc ? -result : result;
}
